import React from 'react';
import styled, { css } from 'styled-components';

import { RewardsRibbon } from 'shared/assets/rewards-ribbon';
import { colors } from 'shared/styles';
import { colors as themeColors } from 'shared/themes';
import type { RewardV2 } from 'src/gql/graphql';
import { Typography } from 'src/typography';
import { LoyaltyPassFooter } from '../loyalty-pass/footer';

type RewardsProps = {
  rewards: Array<Omit<RewardV2, 'brand'>>;
  balance: string;
  programName: string;
};

export const Rewards = ({ rewards = [], balance, programName }: RewardsProps): JSX.Element => {
  const availableRewards = rewards.filter(({ available }) => available);
  return (
    <>
      <BalanceContainer>
        <BalanceContainerRow>
          <RewardsRibbon height={52} width={52} />
          <div>
            <strong>{programName} point balance:</strong> <Balance>{balance} pts</Balance>
          </div>
        </BalanceContainerRow>
        <LoyaltyPassFooter
          containerOverrides={css`
            padding: 16px;
          `}
          borderOverrides={css`
            border-top: 1px solid ${themeColors.blueGrey[80]};
          `}
        />
      </BalanceContainer>

      <StyledHeading tag='h3' size='xsmall' data-testid='loyalty-rewards'>
        Your Rewards
      </StyledHeading>

      {availableRewards.length > 0 ? (
        availableRewards.map(({ copy, costV2 }) => (
          <dl key={copy}>
            <Reward>
              <Description>{copy}</Description>
              <Cost>({costV2} pts)</Cost>
            </Reward>
          </dl>
        ))
      ) : (
        <NoRewardsAvailable>No eligible rewards at this time</NoRewardsAvailable>
      )}
    </>
  );
};

const BalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  background-color: ${({ theme }) => theme.colors.primaryGrey};
  border: 1px solid ${({ theme }) => theme.colors.blueGrey[80]};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.grey[30]};
  margin-bottom: 32px;
`;

const BalanceContainerRow = styled.div`
  align-items: center;
  display: flex;
  padding: 24px 16px;
  gap: 16px;
`;

const Balance = styled.span`
  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: block;
    margin-top: 8px;
  }
`;

const StyledHeading = styled(Typography.Heading)`
  color: ${({ theme }) => theme.colors.grey[30]};
`;

const Reward = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.blueGrey[90]};
  display: flex;
  gap: 8px;
  padding: 16px 0;
`;

const Description = styled.dt`
  font-weight: bold;
`;

const Cost = styled.dd`
  color: ${colors.blueGrey};
  margin: 0;
`;

const NoRewardsAvailable = styled.div`
  margin: 40px 0;
  color: ${colors.blueGrey};
`;
